import { useEffect, useRef, useState } from "react";
import { ApiError } from "../api";

// Read-only status refresh shared by venue entry and the wallet. The caller must
// guard its identity and signal before accepting data; this hook never sends money.
export function useWalletStatusPolling(key: string | null, readStatus: (signal: AbortSignal) => Promise<void>, onSessionLost?: () => void) {
  const reader = useRef(readStatus); reader.current = readStatus;
  const sessionLost = useRef(onSessionLost); sessionLost.current = onSessionLost;
  const [attempt, setAttempt] = useState(0);
  const [notice, setNotice] = useState<{ key: string; text: string } | null>(null);
  useEffect(() => {
    if (!key) return;
    let alive = true, failures = 0, stopped = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let controller: AbortController | null = null;
    const setMessage = (text: string) => { if (alive) setNotice(text ? { key, text } : null); };
    const schedule = (delay = 2500) => {
      clearTimeout(timer);
      if (alive && !stopped && document.visibilityState === "visible" && navigator.onLine) timer = setTimeout(() => void tick(), delay);
    };
    const tick = async () => {
      if (!alive || stopped || controller || document.visibilityState !== "visible" || !navigator.onLine) return;
      const request = new AbortController(); controller = request;
      try {
        await reader.current(AbortSignal.any([request.signal, AbortSignal.timeout(15000)]));
        if (alive && !request.signal.aborted) { failures = 0; setMessage(""); }
      } catch (reason) {
        if (alive && !request.signal.aborted) {
          failures += 1;
          if (reason instanceof ApiError && (reason.status === 401 || reason.status === 403)) sessionLost.current?.();
          stopped = reason instanceof ApiError && (reason.status === 401 || reason.status === 403) || failures >= 3;
          setMessage(reason instanceof ApiError && reason.status === 401 ? "登录已过期，请重新登录后查询原转账。" : reason instanceof ApiError && reason.status === 403 ? "当前会话无权查询，请重新登录后核对原单。" : stopped ? "自动刷新暂时中断，原转账仍保留。可恢复状态更新，请勿重复转账。" : "状态暂未更新，正在重试只读查询；请勿重复转账。");
        }
      } finally {
        if (controller === request) controller = null;
        schedule(Math.min(10000, 2500 * 2 ** failures));
      }
    };
    const visibility = () => {
      if (document.visibilityState !== "visible" || !navigator.onLine) {
        clearTimeout(timer); controller?.abort();
        if (!navigator.onLine) setMessage("网络已断开，原转账仍保留；联网后自动更新，不会重复转账。");
      } else schedule(0);
    };
    setMessage(""); visibility();
    document.addEventListener("visibilitychange", visibility);
    window.addEventListener("online", visibility); window.addEventListener("offline", visibility);
    return () => { alive = false; clearTimeout(timer); controller?.abort(); document.removeEventListener("visibilitychange", visibility); window.removeEventListener("online", visibility); window.removeEventListener("offline", visibility); };
  }, [key, attempt]);
  return { notice: notice?.key === key ? notice.text : "", resume: () => setAttempt(value => value + 1) };
}
